/**
 * Public portal home page.
 * Displays search bar, quick links, digital resources and new arrivals.
 */

import { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { recordTypeIcon } from "@/lib/recordTypes";
import { BookCover } from "../components";
import { DigitalResourcesSection } from "../digital";

interface RecordSummary {
  id: string;
  type: string;
  title: string;
  subtitle: string | null;
  authors: string | null;
  publisher: string | null;
  publicationYear: string | null;
  coverUrl: string | null;
  availableCount: number;
  totalCount: number;
}

interface NewArrivalsResponse {
  records: RecordSummary[];
}

export function HomePage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");

  // Fetch new arrivals
  const { data: newArrivals, isLoading } = useQuery<NewArrivalsResponse>({
    queryKey: ["portal", "new-arrivals"],
    queryFn: async (): Promise<NewArrivalsResponse> => {
      const response = await fetch("/api/portal/catalog/new-arrivals?limit=8");
      if (!response.ok) throw new Error("Failed to fetch");
      return (await response.json()) as NewArrivalsResponse;
    },
  });

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (searchInput.trim()) {
        void navigate(`/opac/search?q=${encodeURIComponent(searchInput.trim())}`);
      }
    },
    [searchInput, navigate]
  );

  return (
    <div className="space-y-12">
      {/* Hero section with search */}
      <section
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 px-6 py-16 text-white sm:px-12 sm:py-24"
        aria-labelledby="hero-title"
      >
        <div className="absolute inset-0 overflow-hidden opacity-10">
          <svg className="h-full w-full" viewBox="0 0 400 400" fill="currentColor">
            <circle cx="50" cy="50" r="40" />
            <circle cx="350" cy="100" r="50" />
            <circle cx="100" cy="300" r="35" />
            <circle cx="300" cy="350" r="45" />
          </svg>
        </div>

        <div className="relative mx-auto max-w-2xl text-center">
          <h1 id="hero-title" className="text-3xl font-bold sm:text-4xl lg:text-5xl">
            Bienvenue à la Bibliothèque
          </h1>
          <p className="mt-4 text-lg text-blue-100 sm:text-xl">
            Explorez notre collection de livres, revues, DVD et bien plus encore.
          </p>

          {/* Search form */}
          <form onSubmit={handleSearch} className="mt-8" role="search">
            <div className="relative">
              <label htmlFor="home-search" className="sr-only">
                Rechercher dans le catalogue
              </label>
              <input
                id="home-search"
                type="search"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                }}
                placeholder="Rechercher par titre, auteur, ISBN..."
                className="w-full rounded-xl border-0 bg-white/10 py-4 pr-4 pl-12 text-lg text-white placeholder-blue-200 backdrop-blur focus:bg-white/20 focus:ring-2 focus:ring-white focus:outline-none"
              />
              <svg
                className="absolute top-1/2 left-4 h-6 w-6 -translate-y-1/2 text-blue-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <button
              type="submit"
              className="mt-4 w-full rounded-xl bg-white py-3 font-medium text-blue-600 hover:bg-blue-50 focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 focus:outline-none sm:w-auto sm:px-8"
            >
              Rechercher
            </button>
          </form>
        </div>
      </section>

      {/* Quick links */}
      <section aria-labelledby="quick-links-title">
        <h2 id="quick-links-title" className="sr-only">
          Accès rapides
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <QuickLinkCard
            to="/opac/search?type=book"
            icon="📚"
            title="Livres"
            description="Romans, essais, documentaires..."
          />
          <QuickLinkCard
            to="/opac/search?type=periodical"
            icon="📰"
            title="Périodiques"
            description="Revues, magazines, journaux..."
          />
          <QuickLinkCard
            to="/opac/search?type=dvd"
            icon="💿"
            title="DVD & CD"
            description="Films, documentaires, musique..."
          />
          <QuickLinkCard
            to="/opac/new-arrivals"
            icon="✨"
            title="Nouveautés"
            description="Dernières acquisitions"
          />
        </div>
      </section>

      {/* Digital resources: one click signs the patron in to the service */}
      <DigitalResourcesSection variant="home" />

      {/* New arrivals */}
      <section aria-labelledby="new-arrivals-title">
        <div className="flex items-center justify-between">
          <h2
            id="new-arrivals-title"
            className="text-2xl font-semibold text-gray-900 dark:text-white"
          >
            Nouveautés
          </h2>
          <Link
            to="/opac/new-arrivals"
            className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Voir tout →
          </Link>
        </div>

        {isLoading ? (
          <div className="mt-6 flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        ) : newArrivals?.records.length ? (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {newArrivals.records.map((record: RecordSummary) => (
              <RecordCard key={record.id} record={record} />
            ))}
          </div>
        ) : (
          <p className="mt-6 text-center text-gray-500 dark:text-gray-400">
            Aucune nouveauté pour le moment.
          </p>
        )}
      </section>

      {/* Call to action */}
      <section
        className="rounded-xl bg-gray-100 p-8 text-center dark:bg-gray-800"
        aria-labelledby="cta-title"
      >
        <h2 id="cta-title" className="text-xl font-semibold text-gray-900 dark:text-white">
          Pas encore inscrit ?
        </h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Rejoignez notre réseau et accédez à toutes nos collections.
        </p>
        <div className="mt-6 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <a
            href="#"
            className="inline-flex items-center rounded-lg bg-blue-600 px-6 py-3 font-medium text-white hover:bg-blue-700"
          >
            S'inscrire
          </a>
          <Link
            to="/opac/login"
            className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-6 py-3 font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Déjà inscrit ? Se connecter
          </Link>
        </div>
      </section>
    </div>
  );
}

interface QuickLinkCardProps {
  to: string;
  icon: string;
  title: string;
  description: string;
}

function QuickLinkCard({ to, icon, title, description }: QuickLinkCardProps) {
  return (
    <Link
      to={to}
      className="group flex flex-col items-center rounded-xl border border-gray-200 bg-white p-6 text-center transition-all hover:border-blue-500 hover:shadow-lg dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-500"
    >
      <span className="text-4xl" role="img" aria-hidden="true">
        {icon}
      </span>
      <h3 className="mt-3 font-semibold text-gray-900 group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
        {title}
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
    </Link>
  );
}

interface RecordCardProps {
  record: RecordSummary;
}

function RecordCard({ record }: RecordCardProps) {
  return (
    <Link
      to={`/opac/record/${record.id}`}
      className="group overflow-hidden rounded-xl border border-gray-200 bg-white transition-all hover:border-blue-500 hover:shadow-lg dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-500"
    >
      {/* Cover */}
      <div className="relative">
        <BookCover
          coverUrl={record.coverUrl}
          alt={record.title}
          type={record.type}
          size="lg"
          className="rounded-none border-0"
        />
        {/* Availability badge */}
        <div
          className={`absolute top-2 right-2 rounded-full px-2 py-1 text-xs font-medium shadow-sm ${
            record.availableCount > 0
              ? "bg-green-100 text-green-800 dark:bg-green-900/80 dark:text-green-300"
              : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/80 dark:text-yellow-300"
          }`}
        >
          {record.availableCount > 0 ? "Disponible" : "Emprunté"}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start gap-2">
          <span className="shrink-0 text-base text-gray-400 dark:text-gray-500" aria-hidden="true">
            {recordTypeIcon(record.type)}
          </span>
          <h3 className="line-clamp-2 font-medium text-gray-900 group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
            {record.title}
          </h3>
        </div>
        {record.authors && (
          <p className="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">{record.authors}</p>
        )}
        {record.publicationYear && (
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{record.publicationYear}</p>
        )}
      </div>
    </Link>
  );
}

export default HomePage;
